import {
  Alert,
  Container,
  Grid,
  Group,
  Spoiler,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';

import { useEffect, useState } from 'react';
import { X } from 'tabler-icons-react';
import { MerchantOnboardingStatus } from '../../enums/merchant-onboarding-status';
import { OpenComponent } from '../../enums/misc.enum';
import { Roles } from '../../enums/roles-enum';
import { retrieveOnboardingResponseToTab } from '../../helpers/merchant-onboarding';
import { parseLink } from '../../helpers/view-information.helper';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import {
  OnboardingInformationByTab,
  RetrieveMerchantResponse,
} from '../../type/admin.types';
import { ErrorResponse } from '../../type/error-response.types';
import ApproveDeclineButton from '../admin-table/components/approve-decline.button';
import DeclineMerchant from '../admin-table/components/decline-merchant.component';

import StatusText from '../admin-table/components/status-text.component';

import { BusinessTypes } from '../../enums/business-type';
import BackButton from '../buttons/back-button.component';
import ApproveEntityModal from '../modals/approve-entity-modal.component';
import './admin-merchant-info.styles.css';
import MerchantInfoBusinessInfo from './components/merchant-info-business-info.component';
import MerchantDocuments from './components/merchant-info-documents.component';
import MerchantInfoGeneral from './components/merchant-info-general.component';
import MerchantInfoIncorporatorsSignatories from './components/merchant-info-incorporators-signatories.component';
import SkeletonMerchantInfoGeneral from './skeleton-loading/skeleton-merchant-info-general.component';
import MerchantDiscountRate from './components/merchant-info-mdr.component';
import { Entity } from '../../enums/entity.enums';

const AdminMerchantInfo = (props: Props) => {
  const {
    merchantId,
    setComponentManager,
    setSelectedUser,
    setRefreshTable,
    refreshTable,
  } = props;

  const axiosPrivate = useAxiosPrivate();

  const [onboardingData, setOnboardingData] =
    useState<OnboardingInformationByTab>();
  const [merchant, setMerchant] = useState<RetrieveMerchantResponse>();
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRetrievingFiles, setIsRetrievingFiles] = useState(false);
  const [isDeclinedModalOpen, setIsDeclinedModalOpen] = useState(false);
  const { auth } = useAuth();

  const fetchMerchant = async () => {
    setIsLoading(true);

    const endpoint = `/web/onboard/${merchantId}`;

    try {
      const result = await axiosPrivate.get(endpoint);
      const onboardingData: RetrieveMerchantResponse = result.data;
      const tabOnboardingData = retrieveOnboardingResponseToTab(
        onboardingData,
        merchantId
      );
      setOnboardingData(tabOnboardingData);
      setMerchant(onboardingData);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-retrieve-onboardingData',
        message: error.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const downloadFiles = async () => {
    setIsRetrievingFiles(true);

    const endpoint = `/web/onboard/${merchantId}/files`;

    try {
      const response = await axiosPrivate.get(endpoint, {
        responseType: 'blob',
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${onboardingData?.general.merchantName ?? merchantId} files.zip`
      );
      link.click();
    } catch (err: any) {
      // @ref https://stackoverflow.com/a/67985097
      let responseObj = await err.response.data.text();

      const error: ErrorResponse = JSON.parse(responseObj);

      showNotification({
        id: 'failed-to-retrieve-onboardingData-files',
        message: error.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsRetrievingFiles(false);
    }
  };

  useEffect(() => {
    fetchMerchant();
  }, [refreshTable]);

  const getIncorporatorSignatoriesTabLabel = (
    onboardingData: OnboardingInformationByTab
  ) => {
    const isStarter =
      onboardingData.general.businessTypeId === BusinessTypes.STARTER;
    const isSoleProprietor =
      onboardingData.general.businessTypeId === BusinessTypes.SOLE_PROPRIETOR;
    if (!isLoading && onboardingData) {
      if (isStarter) return 'Signatories';
      if (isSoleProprietor) return 'Proprietor & Signatories';
      return 'Incorporators & Signatories';
    } else {
      return 'Loading...';
    }
  };

  const isCurrentUserRoleAdmin = auth?.role === Roles.ADMINISTRATOR;
  const isSaveUnderBranch = merchant?.parentTypeId === Entity.BRANCH;

  return (
    <>
      {merchant && isCurrentUserRoleAdmin && (
        <>
          <DeclineMerchant
            entity={merchant}
            handleClose={() => setIsDeclinedModalOpen(false)}
            isDeclinedModalOpen={isDeclinedModalOpen}
            updateTable={setRefreshTable}
          />

          <ApproveEntityModal
            entity={merchant}
            isApproveModalOpen={isApproveModalOpen}
            setIsApproveModalOpen={setIsApproveModalOpen}
            updateTable={setRefreshTable}
          />
        </>
      )}

      <BackButton
        handleClick={() => {
          setComponentManager(OpenComponent.HOME);
          setSelectedUser(undefined);
        }}
      />
      <Title order={2} my="lg">
        Merchant Information
      </Title>

      <Container fluid className="admin-merchant-info" px={0}>
        <>
          {isLoading ? (
            <Stack></Stack>
          ) : (
            <>
              {onboardingData?.disabledReason && !onboardingData?.isActive && (
                <Alert title="Disabled Reason" color="red" mb={22}>
                  <Spoiler
                    maxHeight={110}
                    showLabel={<Text size="sm">Show more</Text>}
                    hideLabel={<Text size="sm">Hide</Text>}
                  >
                    {onboardingData?.disabledReason}
                  </Spoiler>
                </Alert>
              )}
              {(onboardingData?.general.onboardingStatus ===
                MerchantOnboardingStatus.DECLINED ||
                onboardingData?.general.onboardingStatus ===
                  MerchantOnboardingStatus.PENDING) && (
                <Alert
                  data-cy="declinedReason"
                  title="Declined Reason"
                  color="red"
                  mb={22}
                >
                  <Spoiler
                    maxHeight={110}
                    showLabel={<Text size="sm">Show more</Text>}
                    hideLabel={<Text size="sm">Hide</Text>}
                  >
                    {onboardingData.declinedReason}
                  </Spoiler>
                </Alert>
              )}
            </>
          )}

          <Grid>
            <Grid.Col span={6} mb={auth?.role === Roles.ADMINISTRATOR ? 0 : 11}>
              {!isLoading && merchant?.onboardingStatus && (
                <StatusText status={merchant.onboardingStatus} />
              )}
            </Grid.Col>

            {auth?.role === Roles.ADMINISTRATOR && (
              <Grid.Col span={6}>
                <Group position="right">
                  <ApproveDeclineButton
                    setDOpened={setIsDeclinedModalOpen}
                    setAOpened={setIsApproveModalOpen}
                    onboardingStatus={merchant?.onboardingStatus}
                    isLoading={isLoading}
                  />
                </Group>
              </Grid.Col>
            )}
          </Grid>

          <Tabs>
            <Tabs.Tab
              label={'General Information'}
              data-cy="generalInfoTab"
              disabled={isLoading}
            >
              {!isLoading && onboardingData ? (
                <MerchantInfoGeneral
                  generalData={onboardingData.general}
                  parseLink={parseLink}
                />
              ) : (
                <SkeletonMerchantInfoGeneral />
              )}
            </Tabs.Tab>
            {!isLoading &&
              ((onboardingData?.signatories &&
                onboardingData?.signatories.length > 0) ||
                (onboardingData?.incorporators &&
                  onboardingData.incorporators.length > 0)) && (
                <Tabs.Tab
                  data-cy="incorporatorSignatoriesTab"
                  label={getIncorporatorSignatoriesTabLabel(onboardingData)}
                  disabled={isLoading}
                >
                  <MerchantInfoIncorporatorsSignatories
                    signatoriesData={onboardingData.signatories}
                    incorporatorsData={onboardingData.incorporators}
                    businessTypeId={onboardingData.general.businessTypeId}
                  />
                </Tabs.Tab>
              )}
            <Tabs.Tab
              label={'Business Information'}
              data-cy="businessInfoTab"
              disabled={isLoading}
            >
              {!isLoading && onboardingData ? (
                <MerchantInfoBusinessInfo
                  businessData={onboardingData.businessInformation}
                />
              ) : (
                <></>
              )}
            </Tabs.Tab>
            <Tabs.Tab label={'Documents'} disabled={isLoading}>
              {!isLoading && onboardingData ? (
                <MerchantDocuments
                  downloadFiles={downloadFiles}
                  isRetrievingFiles={isRetrievingFiles}
                  files={onboardingData.documents}
                  businessTypeId={onboardingData.general.businessTypeId}
                  merchantId={merchantId}
                />
              ) : (
                <></>
              )}
            </Tabs.Tab>
            {isCurrentUserRoleAdmin && isSaveUnderBranch && (
              <Tabs.Tab label={'MDR'} disabled={isLoading}>
                <MerchantDiscountRate mdrFees={merchant?.mdrFees} />
              </Tabs.Tab>
            )}
          </Tabs>
        </>
      </Container>
    </>
  );
};

export default AdminMerchantInfo;

interface Props {
  merchantId: number;
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
  setSelectedUser: React.Dispatch<React.SetStateAction<number | undefined>>;
  setRefreshTable: React.Dispatch<React.SetStateAction<boolean>>;
  refreshTable: boolean;
}
